import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Callout, Card, H5 } from "@blueprintjs/core";

import "./DiagnosticPage.scss";
import DiagnosticsGrid from "components/diagnostics/DiagnosticsGrid";
import DiagnosticsMenu, { TimeRangeOptions } from "components/diagnostics/DiagnosticsMenu";
import Splitter from "components/Splitter";
import { IdentifiableTraceInfo, QueryInfo } from "types";
import { TimeZoneContext } from "utils/context";
import { getZonedTime, runKey } from "utils/utils";
import { ParseToaster } from "utils/toasters";

export default function DiagnosticPage() {
    const [diagnostic, setDiagnostic] = useState<IdentifiableTraceInfo>();
    const [queryInfo, setQueryInfo] = useState<QueryInfo>();
    const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [searchParams] = useSearchParams();

    const getURLId = useCallback(() => {
        if (searchParams) {
            try {
                const encoded = searchParams.get(runKey);
                if (encoded) {
                    const decoded = atob(encoded);

                    return decoded;
                }
            } catch (e) {
                ParseToaster.show({
                    message: `An error occured parsing run id! Please ensure the URL is valid.`,
                    intent: "danger"
                });
                return undefined;
            }
        }
    }, [searchParams]);

    const getURLRange = useCallback(() => {
        if (searchParams) {
            try {
                const encoded = searchParams.get("range");
                if (encoded) {
                    const decoded = atob(encoded);
                    const parsed = JSON.parse(decoded);
                    return parsed as TimeRangeOptions;
                }
            } catch (e) {
                ParseToaster.show({
                    message: `An error occured parsing range! Please ensure the URL is valid.`,
                    intent: "danger"
                });
            }
        }
    }, [searchParams]);

    return (
        <div className="main-container">
            <TimeZoneContext.Provider value={[timeZone, setTimeZone]}>
                <DiagnosticsMenu id={getURLId()} range={getURLRange()} setQueryInfo={setQueryInfo} />
                <div className="splitter-container">
                    <Splitter
                        primaryFraction={45}
                        primary={
                            <div className="grid-container">
                                <div className="ag-theme-balham-dark ag">
                                    <DiagnosticsGrid queryInfo={queryInfo} setDiagnostic={setDiagnostic} />
                                </div>
                            </div>
                        }
                        secondary={
                            <div className="secondary-card">
                                <Card className="json-viewer">
                                    <H5 className="json-header">
                                        {diagnostic
                                            ? `Time Stamp : ${getZonedTime(diagnostic.timestamp, timeZone)}`
                                            : "Please Select a Run to View"}
                                    </H5>

                                    {diagnostic && (
                                        <>
                                            <div className="message-card">
                                                <Callout>
                                                    <p id="title">
                                                        <u>Message</u>
                                                    </p>
                                                    <p id="message">{`${diagnostic ? diagnostic.message : ""}`}</p>
                                                </Callout>
                                            </div>
                                        </>
                                    )}
                                </Card>
                            </div>
                        }
                    />
                </div>
            </TimeZoneContext.Provider>
        </div>
    );
}
